import { useTranslation } from "react-i18next";
import type { TellescopeFormFieldBaseProps } from "../../types";
import { useState } from "react";
import { Label } from "@tellescope/design-system";
import { Button, cn, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger, useStateWithSetAction } from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { type TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { FieldDescription } from "../../FieldDescription";
import { SelectedAllergyEditor } from "./SelectedAllergyEditor";
import { useAllergyCatalog } from "./useAllergyCatalog";

export interface AllergiesFieldProps extends TellescopeFormFieldBaseProps<"Allergies"> {
  allergies?: TellescopeModelFromName<"allergy_codes">[];
  customFilter?: (
    searchTerm: string,
    allergies: TellescopeModelFromName<"allergy_codes">[]
  ) => TellescopeModelFromName<"allergy_codes">[];
}

export const AllergiesField = ({
  title,
  description,
  htmlDescription,
  placeholder,
  value,
  onChange,
  className,
  allergies,
  customFilter,
  options,
  onBlur,
  error,
  required,
  autoFocus,
}: AllergiesFieldProps) => {
  const { t } = useTranslation("forms");
  const [open, setOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      onBlur?.();
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);
  const { options: allergyOptions, isLoading } = useAllergyCatalog({
    dataSource: options?.dataSource,
    debouncedSearchQuery,
    allergies,
    customFilter,
  });

  return (
    <div className="space-y-1">
      <Label required={required}>{title || placeholder || t("Choose allergies...")}</Label>
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className={className}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={placeholder ?? t("Choose allergies...")}
            className="w-full justify-between font-normal"
            autoFocus={autoFocus}
          >
            <span className="text-muted-foreground">{placeholder ?? t("Choose allergies...")}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder={t("Search allergies...")}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>
                {isLoading && allergies?.length === undefined ? t("Loading allergies...") : t("No allergies found")}
              </CommandEmpty>
              <CommandGroup>
                {allergyOptions.map((allergy) => {
                  const isSelected = !!value?.some((item) => item.code === allergy.code);
                  return (
                    <CommandItem
                      key={allergy.id}
                      value={allergy.code}
                      onSelect={() => {
                        if (isSelected) {
                          onChange(value?.filter((item) => item.code !== allergy.code) ?? []);
                        } else {
                          onChange([
                            ...(value ?? []),
                            { code: allergy.code, display: allergy.display, system: allergy.system },
                          ]);
                        }
                      }}
                    >
                      <div className="flex flex-col gap-1 w-full">
                        <div className="font-medium text-sm truncate">{allergy.display}</div>
                        <div className="text-xs text-muted-foreground truncate">{allergy.code}</div>
                      </div>
                      <Check className={cn("ml-auto h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {!!value?.length && (
        <div className="flex flex-col gap-2 mt-2">
          {value.map((allergy, index) => (
            <SelectedAllergyEditor
              key={allergy.code}
              allergy={allergy}
              onChange={(updated) => onChange(value.map((item, i) => (i === index ? updated : item)))}
              onRemove={() => onChange(value.filter((_, i) => i !== index))}
            />
          ))}
        </div>
      )}
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
